import React from 'react';
import { Alert, Box, Chip, CircularProgress, Paper, Stack, Typography, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { fetchDialOpReportCard } from '../../../services/dialingOpportunityService';
import type {
  DialOpCallType,
  DialOpLeadPool,
  DialOpReportCardRow,
} from '../../../types/dialingOpportunity';
import SegmentToggle from './SegmentToggle';
import { buildDialOpGridSx, DialOpGridToolbar, HeaderWithTooltip } from './dialOpTableKit';

const PAGE_SIZE = 25;

function formatLocalHour(hour?: number | null): string {
  if (hour === null || hour === undefined) return '—';
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12} ${period}`;
}

interface ReportCardTableProps {
  currentStart: string;
  currentEnd: string;
  callType: DialOpCallType;
  leadPool: DialOpLeadPool;
  stateCd?: string[] | null;
  agent?: string[] | null;
  onCallTypeChange?: (value: DialOpCallType) => void;
  onLeadPoolChange?: (value: DialOpLeadPool) => void;
}

export default function ReportCardTable({
  currentStart,
  currentEnd,
  callType,
  leadPool,
  stateCd,
  agent,
  onCallTypeChange,
  onLeadPoolChange,
}: ReportCardTableProps) {
  const [rows, setRows] = React.useState<DialOpReportCardRow[]>([]);
  const [page, setPage] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [hasMore, setHasMore] = React.useState(false);
  const theme = useTheme();

  // Stable string keys for the effect deps below -- avoids re-fetching when a
  // caller passes a freshly-constructed array with the same states/agents in it.
  const stateKey = (stateCd ?? []).join(',');
  const agentKey = (agent ?? []).join(',');

  React.useEffect(() => {
    setPage(0);
  }, [currentStart, currentEnd, callType, leadPool, stateKey, agentKey]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Fetch one extra row to know whether a next page exists, without a
    // separate count query against the warehouse.
    fetchDialOpReportCard({
      currentStart,
      currentEnd,
      callType,
      leadPool,
      state: stateCd,
      agent,
      limit: PAGE_SIZE + 1,
      offset: page * PAGE_SIZE,
    })
      .then((resp) => {
        if (cancelled) return;
        if (resp.message) {
          setError(resp.message);
          setRows([]);
          setHasMore(false);
          return;
        }
        setHasMore(resp.rows.length > PAGE_SIZE);
        setRows(resp.rows.slice(0, PAGE_SIZE));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load report card');
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStart, currentEnd, callType, leadPool, stateKey, agentKey, page]);

  const isAppointmentView = callType === 'scheduled_appointment';

  const gridRows = React.useMemo(
    () =>
      rows.map((row, idx) => ({
        id: `${row.hubspot_contact_id ?? idx}-${row.lead_dt ?? idx}`,
        ...row,
      })),
    [rows],
  );

  const columns = React.useMemo<GridColDef[]>(() => {
    const base: GridColDef[] = [
      { field: 'lead_dt', headerName: 'Lead date', minWidth: 108, flex: 0.8 },
      { field: 'state_cd', headerName: 'State', minWidth: 64, flex: 0.5 },
      {
        field: 'tier',
        headerName: 'Tier',
        minWidth: 64,
        flex: 0.5,
        renderCell: (params: GridRenderCellParams) =>
          params.value === null || params.value === undefined ? 'Unscored' : `Tier ${params.value}`,
      },
      { field: 'pool', headerName: 'Pool', minWidth: 72, flex: 0.5 },
      {
        field: 'dialed',
        headerName: 'Dialed',
        minWidth: 80,
        flex: 0.5,
        renderCell: (params: GridRenderCellParams) => (params.value ? 'Yes' : 'No'),
      },
      { field: 'dial_dt', headerName: 'Dial date', minWidth: 108, flex: 0.8 },
      {
        field: 'dial_local_hr',
        headerName: 'Dial time',
        minWidth: 96,
        flex: 0.6,
        renderCell: (params: GridRenderCellParams) => formatLocalHour(params.value as number | null),
      },
    ];

    if (isAppointmentView) {
      base.push(
        {
          field: 'contacted',
          headerName: 'Kept',
          minWidth: 80,
          flex: 0.5,
          renderHeader: () => (
            <HeaderWithTooltip label="Kept" tooltip="Appointment call connected for 90+ seconds." />
          ),
          renderCell: (params: GridRenderCellParams) => (params.value ? 'Kept' : 'Missed'),
        },
      );
    } else {
      base.push(
        {
          field: 'best_hour',
          headerName: 'Best hour',
          minWidth: 96,
          flex: 0.6,
          renderCell: (params: GridRenderCellParams) => formatLocalHour(params.value as number | null),
        },
        {
          field: 'deviation_hours',
          headerName: 'Deviation',
          minWidth: 96,
          flex: 0.6,
          renderHeader: () => (
            <HeaderWithTooltip label="Deviation" tooltip="Hours away from this state's best call hour." />
          ),
          renderCell: (params: GridRenderCellParams) =>
            params.value === null || params.value === undefined ? '—' : `${params.value}h`,
        },
        {
          field: 'contacted',
          headerName: 'Contacted',
          minWidth: 96,
          flex: 0.5,
          renderCell: (params: GridRenderCellParams) => (params.value ? 'Yes' : 'No'),
        },
      );
    }

    base.push(
      { field: 'call_type_class', headerName: 'Call type', minWidth: 140, flex: 0.8 },
      {
        field: 'cas_outcome',
        headerName: 'CAS',
        minWidth: 72,
        flex: 0.5,
        renderCell: (params: GridRenderCellParams) => (params.value ? 'Yes' : ''),
      },
      { field: 'dial_agent_full_nm', headerName: 'Agent', minWidth: 160, flex: 1 },
    );
    return base;
  }, [isAppointmentView]);

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        overflow: 'hidden',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
      })}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700}>
            Lead-level detail
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={isAppointmentView ? 'Appointment adherence' : 'First-call detail'}
            sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
          />
          {stateCd?.length ? (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`State: ${stateCd.join(', ')}`}
              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
            />
          ) : null}
          {agent?.length ? (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`Agent: ${agent.join(', ')}`}
              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
            />
          ) : null}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
          One row per lead behind the numbers above. Use the toggles to switch which slice you&apos;re
          drilling into; search to find a state or agent within the current page.
        </Typography>
        <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap alignItems="center">
          {onCallTypeChange ? (
            <SegmentToggle<DialOpCallType>
              label="Call type"
              value={callType}
              onChange={onCallTypeChange}
              options={[
                { value: 'all', label: 'All' },
                {
                  value: 'first_call_connect',
                  label: 'First call',
                  tooltip: 'The lead’s first outbound dial within their 15-day window.',
                },
                {
                  value: 'scheduled_appointment',
                  label: 'Scheduled appt',
                  tooltip: 'A call after the lead was booked into a scheduled appointment.',
                },
              ]}
            />
          ) : null}
          {onLeadPoolChange ? (
            <SegmentToggle<DialOpLeadPool>
              label="Lead pool"
              value={leadPool}
              onChange={onLeadPoolChange}
              options={[
                { value: 'all', label: 'All' },
                { value: 'fresh', label: 'Fresh', tooltip: 'Days 0–7 from the lead date.' },
                {
                  value: 'sold',
                  label: 'Sold',
                  tooltip: 'Resold within the lead’s 15-day reachability window.',
                },
              ]}
            />
          ) : null}
        </Stack>
      </Box>

      {error ? (
        <Alert severity="warning" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : (
        <Box sx={{ width: '100%', minHeight: 420, position: 'relative' }}>
          <DataGrid
            rows={gridRows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            hideFooter
            localeText={{ toolbarQuickFilterPlaceholder: 'Search this page…' }}
            slots={{ toolbar: DialOpGridToolbar }}
            sx={buildDialOpGridSx(theme)}
            autoHeight
          />
          {/* Server-paginated (warehouse-backed, unknown total count) -- can't use
              the DataGrid's built-in client pager like the other tables, but this
              footer mirrors their chrome (same border/height/type scale) so the
              page reads as one consistent table style. */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 2, minHeight: 52, borderTop: `1px solid ${theme.palette.divider}` }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              Page {page + 1}
              {loading ? (
                <CircularProgress size={12} sx={{ ml: 1, verticalAlign: 'middle' }} />
              ) : null}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip
                size="small"
                label="Prev"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                clickable
              />
              <Chip
                size="small"
                label="Next"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore || loading}
                clickable
              />
            </Stack>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
